import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { API, formatLei } from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ceva n-a mers bine. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "h-11 rounded-xl border border-white/15 bg-hydro-navy/60 px-3 text-right font-mono text-sm font-bold text-white outline-none focus:border-hydro-cyan";

export default function AdminPricingPanel() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/admin/pricing`, { withCredentials: true })
      .then((r) => setConfig(r.data))
      .catch(() => {
        setError("Nu am putut încărca prețurile. Apasă Reîmprospătează sau reîncarcă pagina.");
      });
  }, []);

  if (!config) {
    return (
      <div className="py-20 text-center">
        {error ? (
          <p data-testid="admin-pricing-load-error" className="text-sm font-semibold text-red-400">{error}</p>
        ) : (
          <div className="flex items-center justify-center gap-3 text-sm text-white/50">
            <Loader2 size={18} className="animate-spin" />
            Se încarcă prețurile...
          </div>
        )}
      </div>
    );
  }

  const setBase = (v) => setConfig((c) => ({ ...c, base_rate_per_meter: v }));
  const setCasing = (id, v) =>
    setConfig((c) => ({
      ...c,
      casings: c.casings.map((x) => (x.id === id ? { ...x, extra_per_meter: v } : x)),
    }));
  const setTransport = (i, v) =>
    setConfig((c) => ({
      ...c,
      localities: c.localities.map((x, idx) => (idx === i ? { ...x, transport: v } : x)),
    }));

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        base_rate_per_meter: Number(config.base_rate_per_meter),
        casings: config.casings.map((c) => ({
          id: c.id,
          extra_per_meter: Number(c.extra_per_meter),
        })),
        localities: config.localities.map((l) => ({
          name: l.name,
          group: l.group,
          transport: Number(l.transport),
        })),
      };
      const { data } = await axios.put(`${API}/admin/pricing`, payload, { withCredentials: true });
      setConfig(data);
      toast.success("Prețuri actualizate!", {
        description: "Calculatorul de pe site folosește acum noile prețuri.",
      });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setError(msg);
      toast.error("Nu am putut salva prețurile.", { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pricing-panel">
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-extrabold text-white">Preț pe metru forat</h2>
            <p className="mt-1 text-sm text-white/50">
              Se aplică la fiecare metru, cu țeava Ø125 inclusă. Actual: {formatLei(config.base_rate_per_meter)} lei/m.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="10"
              max="1000"
              data-testid="admin-pricing-base-rate"
              value={config.base_rate_per_meter}
              onChange={(e) => setBase(e.target.value)}
              className={`${inputCls} w-28`}
              aria-label="Preț pe metru forat"
            />
            <span className="text-sm font-bold text-white/50">lei/m</span>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
        <h2 className="font-display text-xl font-extrabold text-white">Țevi PVC albastre cu filet</h2>
        <p className="mt-1 text-sm text-white/50">Majorarea se adaugă la prețul pe metru pentru diametre mai mari.</p>
        <div className="mt-5 space-y-3">
          {config.casings.map((c) => (
            <div
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/[0.03] px-4 py-3"
            >
              <div>
                <p className="font-bold text-white">{c.label}</p>
                <p className="text-xs text-white/40">
                  {Number(c.extra_per_meter) === 0 ? "inclusă în preț" : `+${c.extra_per_meter} lei / metru`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="500"
                  data-testid={`admin-pricing-casing-${c.id}`}
                  value={c.extra_per_meter}
                  onChange={(e) => setCasing(c.id, e.target.value)}
                  className={`${inputCls} w-24`}
                  aria-label={`Majorare ${c.label}`}
                />
                <span className="text-sm font-bold text-white/50">lei/m</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
        <h2 className="font-display text-xl font-extrabold text-white">Deplasare pe localitate</h2>
        <p className="mt-1 text-sm text-white/50">Costul de deplasare adăugat la estimare, pe fiecare localitate.</p>
        <div className="mt-5 max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {config.localities.map((l, i) => (
            <div
              key={l.name}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/[0.03] px-4 py-2.5"
            >
              <div className="mr-auto">
                <p className="text-sm font-semibold text-white/85">{l.name}</p>
                <p className="text-[11px] font-mono uppercase tracking-widest text-white/35">{l.group}</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="2000"
                  data-testid={`admin-pricing-transport-${i}`}
                  value={l.transport}
                  onChange={(e) => setTransport(i, e.target.value)}
                  className={`${inputCls} w-24`}
                  aria-label={`Deplasare ${l.name}`}
                />
                <span className="text-sm font-bold text-white/50">lei</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <p data-testid="admin-pricing-error" className="text-sm font-semibold text-red-400">
          {error}
        </p>
      )}

      <div className="sticky bottom-4 z-10">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-pricing-save"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-hydro-cyan px-7 py-4 text-base font-extrabold text-hydro-navy shadow-[0_10px_40px_rgba(0,180,216,0.35)] transition-colors hover:bg-white disabled:opacity-60 sm:w-auto"
        >
          <Save size={17} />
          {saving ? "Se salvează..." : "Salvează prețurile"}
        </button>
      </div>
    </div>
  );
}
